import { useQueryClient } from '@tanstack/vue-query';
import { useCookieRedirect } from '~/composables/useCookieRedirect';
import { useGlobalMerchant } from '~/composables/useGlobalMerchant';
import { useGlobalMerchantError } from '~/composables/useGlobalMerchantError';
import { useGlobalMerchantRelationship } from '~/composables/useGlobalMerchantRelationship';
import { useMerchantSlug } from '~/composables/useMerchantSlug';
import { useIsMerchantSwitching } from '~/composables/useMerchantSwitch';
import { useVerifiedSupabaseUser } from '~/composables/useVerifiedSupabaseUser';
import { isRedirectError } from '~/utils/errors';
import { fetchMerchantContext, checkMerchantAccess } from '~/utils/merchant';

export default defineNuxtRouteMiddleware(async (to) => {
	const merchantSlug = useMerchantSlug(to);

	if (!merchantSlug.value) {
		return;
	}

	const queryClient = useQueryClient();
	const user = useVerifiedSupabaseUser();

	const globalMerchant = useGlobalMerchant();

	// Unmount the previous space's page before the merchant changes, otherwise its
	// queries refetch with the new merchantId and the old route params.
	if (globalMerchant.value && globalMerchant.value.slug !== merchantSlug.value) {
		useIsMerchantSwitching().value = true;
		await nextTick();
	}

	const { merchant, relationship } = await fetchMerchantContext({
		queryClient,
		merchantSlug: merchantSlug.value,
		user: user.value,
	});

	globalMerchant.value = merchant;

	const globalMerchantRelationship = useGlobalMerchantRelationship();
	globalMerchantRelationship.value = relationship;

	const accessResult = checkMerchantAccess({
		isPublicPage: to.meta.isPublicMerchantPage ?? false,
		merchant,
		user: user.value,
		relationship,
	});

	const merchantError = useGlobalMerchantError();

	if (accessResult.isErr()) {
		if (isRedirectError(accessResult.error)) {
			const redirectInfo = useCookieRedirect();
			redirectInfo.path.value = to.path;
			return navigateTo(accessResult.error.target);
		}

		merchantError.value = accessResult.error;

		return;
	}

	// [merchantSlug].vue's afterEach can't do this during a switch – it's unmounted.
	merchantError.value = null;
});
